// src/pages/UserCreation/UserCreation.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faUserEdit,
  faUserShield,
  faIdBadge,
  faPlusSquare,
  faUserLock,
  faShieldAlt,
  faUserTie,
  faUsers,
  faStore,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';
import { LEVEL_NAMES } from '../../constants/userLevels';

// Define the User Creation options
const userCreationOptions = [
  {
    title: 'Create Admin User',
    icon: faUserPlus,
    path: '/adminControl/admins/create',
    perm: 'users.admins',
    description: 'Create new admin user accounts'
  },
  // {
  //   title: 'Manage Users',
  //   icon: faUserEdit,
  //   path: '/adminControl/users/manage',
  //   modules: [1311],
  //   description: 'Edit and manage existing users'
  // },
  // {
  //   title: 'Create MSB',
  //   icon: faIdBadge,
  //   path: '/create-msb',
  //   modules: [1312],
  //   description: 'Configure user groups and teams'
  // },
  // {
  //   title: 'User Permissions',
  //   icon: faUserShield,
  //   path: '/adminControl/users/permissions',
  //   modules: [1313],
  //   description: 'Set custom permissions for users'
  // },
  {
    title: 'Assign Pincodes',
    icon: faPlusSquare,
    path: '/adminControl/admins/assign-pincodes',
    perm: 'users.pincodes',
    description: 'Assign locations to users'
  },
  {
    title: 'Assign Designation',
    icon: faUserLock,
    path: '/adminControl/admins/assign-privileges',
    perm: 'users.designations',
    description: 'Grant specific privileges to users'
  },
  {
    title: `${LEVEL_NAMES[2]}s`,
    icon: faUserTie,
    path: '/adminControl/admins/state-heads',
    perm: 'users.stateHeads',
    description: `View and manage ${LEVEL_NAMES[2]} users`
  },
  {
    title: `${LEVEL_NAMES[3]}s`,
    icon: faUsers,
    path: '/adminControl/admins/state-staff',
    perm: 'users.stateStaff',
    description: `View and manage ${LEVEL_NAMES[3]} users`
  },
  {
    title: `${LEVEL_NAMES[4]}s`,
    icon: faStore,
    path: '/adminControl/admins/qpay-centres',
    perm: 'users.qpayCentres',
    description: `View and manage ${LEVEL_NAMES[4]} users`
  },
  {
    title: `${LEVEL_NAMES[5]}s`,
    icon: faUserCheck,
    path: '/adminControl/admins/qpay-agents',
    perm: 'users.qpayAgents',
    description: `View and manage ${LEVEL_NAMES[5]} users`
  },
  // {
  //   title: 'Remove Privileges',
  //   icon: faShieldAlt,
  //   path: '/adminControl/admins/remove-privileges',
  //   modules: [1316],
  //   description: 'Configure special access rights'
  // }
];

const UserCreation = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = userCreationOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access user management features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        User Management
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {userCreationOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      color="#FFA500"
                      style={{ marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default UserCreation;